import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RecurringSeriesDetail, RecurringSeriesOccurrence } from '@spike/new-booking-model';
import clsx from 'clsx';
import PriceFormat from 'components/UI/PriceFormat';
import { Pagination } from 'components/UI/V3/Pagination/Pagination';
import { SelectDropdown } from 'components/UI/V3/SelectDropdown/SelectDropdown';
import { Spinner } from 'components/UI/V3/Spinner/Spinner';
import { Table } from 'components/UI/V3/Table/Table';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { ComponentProps, FunctionComponent, KeyboardEvent } from 'react';
import { MISSING_VALUE_PLACEHOLDER } from '../../utils/recurringSeries';
import SeriesBookingStatusPill from './SeriesBookingStatusPill';
import { useRecurringSeriesBookings } from './useRecurringSeriesBookings';
import { SeriesBookingsFilter } from './useRecurringSeriesView';
import styles from './RecurringSeriesView.module.scss';

interface BookingsSeriesSectionProps {
    detail: RecurringSeriesDetail;
    isPageLoading: boolean;
    filter: SeriesBookingsFilter;
    onRequestPage: (page: number) => void;
    onChangeFilter: (filter: SeriesBookingsFilter) => void;
    selectedBookingId?: number;
    onSelectBooking?: (occurrence: RecurringSeriesOccurrence) => void;
}

const formatDateRange = (occurrence: RecurringSeriesOccurrence): string => {
    if (occurrence.from.isSame(occurrence.to, 'day')) {
        return occurrence.from.format('MMM D, YYYY');
    }

    if (occurrence.from.isSame(occurrence.to, 'month')) {
        return `${occurrence.from.format('MMM D')}-${occurrence.to.format('D, YYYY')}`;
    }

    return `${occurrence.from.format('MMM D')} - ${occurrence.to.format('MMM D, YYYY')}`;
};

export const BookingsSeriesSection: FunctionComponent<BookingsSeriesSectionProps> = props => {
    const bookings = useRecurringSeriesBookings(props.detail, props.filter, props.onRequestPage);

    const renderRow = (occurrence: RecurringSeriesOccurrence) => {
        const onSelectBooking = props.onSelectBooking;
        const isSelected = occurrence.bookingId !== undefined && occurrence.bookingId === props.selectedBookingId;
        const isSelectable = !!onSelectBooking && occurrence.bookingId !== undefined && !isSelected;

        const interactionProps: ComponentProps<'tr'> =
            isSelectable && onSelectBooking
                ? {
                      role: 'button',
                      tabIndex: 0,
                      onClick: () => onSelectBooking(occurrence),
                      onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onSelectBooking(occurrence);
                          }
                      }
                  }
                : {};

        return (
            <TableRow
                key={occurrence.index}
                className={clsx({
                    [styles.rowSelectable]: isSelectable,
                    [styles.rowSelected]: isSelected && !!onSelectBooking
                })}
                aria-current={isSelected && !!onSelectBooking ? 'true' : undefined}
                {...interactionProps}
            >
                <TableCell>Booking {occurrence.index + 1}</TableCell>
                <TableCell>{formatDateRange(occurrence)}</TableCell>
                <TableCell>{occurrence.invoiceNumber ?? MISSING_VALUE_PLACEHOLDER}</TableCell>
                <TableCell>
                    <SeriesBookingStatusPill status={occurrence.status} />
                </TableCell>
                <TableCell>
                    {occurrence.amount === undefined ? (
                        MISSING_VALUE_PLACEHOLDER
                    ) : (
                        <PriceFormat price={occurrence.amount.toFixed(2)} />
                    )}
                </TableCell>
            </TableRow>
        );
    };

    return (
        <>
            <h3 className={styles.sectionTitle}>Bookings Series</h3>

            <div className={styles.filterContainer}>
                <SelectDropdown<SeriesBookingsFilter>
                    options={bookings.options}
                    value={bookings.selected}
                    dropdownProps={{
                        align: 'start',
                        zIndex: 1301,
                        minWidth: 208,
                        maxHeight: 320,
                        className: styles.filterDropdown
                    }}
                    trigger={
                        <div className={styles.filter}>
                            <span className={styles.filterLabel}>{bookings.selected.label}</span>
                            <FontAwesomeIcon
                                icon={faAngleDown}
                                className={styles.filterIcon}
                            />
                        </div>
                    }
                    onChange={option => props.onChangeFilter(option?.id ?? 'all')}
                />
            </div>

            <div className={styles.tableContainer}>
                <Table
                    firstColumnFixed
                    className={clsx(styles.table, { [styles.tableLoading]: props.isPageLoading })}
                    wrapperClassName={styles.tableScroll}
                >
                    <TableHeader>
                        <TableHeaderCell>Booking</TableHeaderCell>
                        <TableHeaderCell>Date Range</TableHeaderCell>
                        <TableHeaderCell>Invoice</TableHeaderCell>
                        <TableHeaderCell>Status</TableHeaderCell>
                        <TableHeaderCell>Amount</TableHeaderCell>
                    </TableHeader>
                    <TableBody>{bookings.rows.map(renderRow)}</TableBody>
                </Table>

                {props.isPageLoading && (
                    <div className={styles.tableSpinner}>
                        <Spinner size={33} />
                    </div>
                )}
            </div>

            <div className={styles.listFooter}>
                <p className={styles.count}>
                    Showing {bookings.firstShown}-{bookings.lastShown} of {bookings.total} bookings
                </p>

                {bookings.totalPages > 1 && (
                    <Pagination
                        page={bookings.page}
                        totalPages={bookings.totalPages}
                        isLoading={props.isPageLoading}
                        onPageChange={bookings.changePage}
                    />
                )}
            </div>
        </>
    );
};

export default BookingsSeriesSection;
